// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.0.4
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'
import toast from 'react-hot-toast'

// Inactivity timeout in milliseconds (15 minutes)
const INACTIVITY_TIMEOUT = 15 * 60 * 1000
// Warning before logout (1 minute before)
const WARNING_BEFORE_LOGOUT = 60 * 1000

interface User {
  id: string
  email: string
  company_name?: string
  contact_name?: string
  email_verified: boolean
  two_factor_enabled: boolean
  role?: string
  msp_account_id?: string | null
  msp_account_name?: string | null
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string, twoFactorCode?: string) => Promise<{ requires2FA?: boolean }>
  register: (data: RegisterData) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

interface RegisterData {
  email: string
  password: string
  company_name?: string
  contact_name?: string
  phone?: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  // Inactivity timer refs
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningShownRef = useRef(false)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    const token = localStorage.getItem('portal_token')
    if (!token) {
      setIsLoading(false)
      return
    }

    try {
      const response = await api.get('/api/portal/auth/me')
      setUser(response.data)
    } catch (error) {
      localStorage.removeItem('portal_token')
      localStorage.removeItem('portal_refresh_token')
    } finally {
      setIsLoading(false)
    }
  }

  // Clear all inactivity timers
  const clearInactivityTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current)
      warningTimerRef.current = null
    }
    warningShownRef.current = false
  }, [])

  // Handle auto logout due to inactivity
  const handleInactivityLogout = useCallback(() => {
    clearInactivityTimers()
    localStorage.removeItem('portal_token')
    localStorage.removeItem('portal_refresh_token')
    setUser(null)
    navigate('/login')
    toast.error('You have been logged out due to inactivity', { duration: 5000 })
  }, [navigate, clearInactivityTimers])

  // Reset the inactivity timer
  const resetInactivityTimer = useCallback(() => {
    // Only track if user is authenticated
    if (!user) return

    clearInactivityTimers()

    // Set warning timer (fires 1 minute before logout)
    warningTimerRef.current = setTimeout(() => {
      if (!warningShownRef.current) {
        warningShownRef.current = true
        toast('You will be logged out in 1 minute due to inactivity', {
          icon: '⚠️',
          duration: 10000
        })
      }
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT)

    // Set logout timer
    inactivityTimerRef.current = setTimeout(() => {
      handleInactivityLogout()
    }, INACTIVITY_TIMEOUT)
  }, [user, clearInactivityTimers, handleInactivityLogout])

  // Set up activity listeners when user is authenticated
  useEffect(() => {
    if (!user) {
      clearInactivityTimers()
      return
    }

    // Activity events to track
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click']

    // Throttle the reset to avoid too many timer resets
    let lastActivity = Date.now()
    const throttledReset = () => {
      const now = Date.now()
      // Only reset if at least 1 second has passed since last reset
      if (now - lastActivity > 1000) {
        lastActivity = now
        warningShownRef.current = false
        resetInactivityTimer()
      }
    }

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, throttledReset, { passive: true })
    })

    // Start the initial timer
    resetInactivityTimer()

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, throttledReset)
      })
      clearInactivityTimers()
    }
  }, [user, resetInactivityTimer, clearInactivityTimers])

  const login = async (email: string, password: string, twoFactorCode?: string) => {
    const response = await api.post('/api/portal/auth/login', {
      email,
      password,
      two_factor_code: twoFactorCode
    })

    if (response.data.requires_2fa) {
      return { requires2FA: true }
    }

    const { access_token, refresh_token, user: userData } = response.data
    localStorage.setItem('portal_token', access_token)
    localStorage.setItem('portal_refresh_token', refresh_token)
    setUser(userData)
    navigate(userData?.role === 'msp' ? '/msp' : '/dashboard')
    toast.success('Welcome back!')
    return {}
  }

  const register = async (data: RegisterData) => {
    await api.post('/api/portal/auth/register', data)
    toast.success('Registration successful! Please check your email to verify your account.')
    navigate('/login')
  }

  const logout = async () => {
    // Clear inactivity timers
    clearInactivityTimers()

    try {
      await api.post('/api/portal/auth/logout')
    } catch (error) {
      // Ignore errors
    }
    localStorage.removeItem('portal_token')
    localStorage.removeItem('portal_refresh_token')
    setUser(null)
    navigate('/login')
    toast.success('Logged out successfully')
  }

  const refreshUser = async () => {
    try {
      const response = await api.get('/api/portal/auth/me')
      setUser(response.data)
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      register,
      logout,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
